import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField, Grid, Chip, IconButton } from '@mui/material';
import { Add } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { ICompany } from '../../../redux/company/types';
import { updateCompany } from '../../../redux/company/actions';
import { RichTextEditor } from '../../Common/RichTextEditor';
import { COMPANY_BRAND_ORANGE } from '../../../constants/companyBrand';

interface ICompanyAboutEdit {
    company?: ICompany;
    isEditing: boolean;
    onSave: (saveFunction: () => void) => void;
    onChanges: (hasChanges: boolean) => void;
}

const SURFACE_CARD = '#1a1a1a';
const SURFACE_CHIP = '#262626';
const LABEL_MUTED = 'rgba(255,255,255,0.45)';

const CompanyAboutEdit: React.FC<ICompanyAboutEdit> = ({ company, isEditing, onSave, onChanges }) => {
    const dispatch = useDispatch();
    const [formData, setFormData] = useState({
        about: company?.about || '',
        perks: company?.perks || [],
        values: company?.values || [],
    });
    const [initialData, setInitialData] = useState(formData);
    const [newPerk, setNewPerk] = useState('');
    const [newValue, setNewValue] = useState('');

    useEffect(() => {
        if (company) {
            const newData = {
                about: company.about || '',
                perks: company.perks || [],
                values: company.values || [],
            };
            setFormData(newData);
            setInitialData(newData);
        }
    }, [company]);

    useEffect(() => {
        const hasChanges = JSON.stringify(formData) !== JSON.stringify(initialData);
        onChanges(hasChanges);
    }, [formData, initialData, onChanges]);

    const handleFieldChange = (field: string, value: unknown) => {
        setFormData(prev => ({
            ...prev,
            [field]: value,
        }));
    };

    const addPerk = () => {
        if (newPerk.trim() && !formData.perks.includes(newPerk.trim())) {
            handleFieldChange('perks', [...formData.perks, newPerk.trim()]);
            setNewPerk('');
        }
    };

    const removePerk = (perkToRemove: string) => {
        handleFieldChange(
            'perks',
            formData.perks.filter(perk => perk !== perkToRemove),
        );
    };

    const addValue = () => {
        if (newValue.trim() && !formData.values.includes(newValue.trim())) {
            handleFieldChange('values', [...formData.values, newValue.trim()]);
            setNewValue('');
        }
    };

    const removeValue = (valueToRemove: string) => {
        handleFieldChange(
            'values',
            formData.values.filter(value => value !== valueToRemove),
        );
    };

    const handleSave = () => {
        if (company?._id) {
            const payload = {
                id: company._id,
                body: {
                    about: formData.about,
                    perks: formData.perks,
                    values: formData.values,
                },
            };
            dispatch(updateCompany(payload));
        }
    };

    useEffect(() => {
        onSave(handleSave);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onSave, formData, company?._id]);

    const perkChipSx = {
        height: 32,
        borderRadius: 999,
        bgcolor: 'rgba(255, 156, 40, 0.12)',
        color: COMPANY_BRAND_ORANGE,
        border: '1px solid rgba(255, 156, 40, 0.35)',
        fontWeight: 600,
        fontSize: 12,
        fontFamily: FONT_ENUM.MAIN_MEDIUM,
        '& .MuiChip-deleteIcon': {
            color: COMPANY_BRAND_ORANGE,
            '&:hover': { color: '#ffb04d' },
        },
    };

    const valueChipSx = {
        height: 32,
        borderRadius: 999,
        bgcolor: SURFACE_CHIP,
        color: '#ffffff',
        border: '1px solid rgba(255,255,255,0.15)',
        fontWeight: 600,
        fontSize: 12,
        fontFamily: FONT_ENUM.MAIN_MEDIUM,
        '& .MuiChip-deleteIcon': {
            color: 'rgba(255,255,255,0.85)',
            '&:hover': { color: '#ffffff' },
        },
    };

    const dashedAddRowSx = {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        border: '1px dashed rgba(255,255,255,0.35)',
        borderRadius: 999,
        px: 1.5,
        py: 0.25,
        bgcolor: '#000000',
        minHeight: 36,
        flex: '1 1 180px',
        maxWidth: 320,
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box>
                <Typography
                    component="h1"
                    sx={{
                        fontFamily: FONT_ENUM.BOLD_HEADER,
                        fontWeight: 800,
                        fontSize: { xs: '1.75rem', md: '2.25rem' },
                        color: '#ffffff',
                        letterSpacing: '-0.02em',
                        mb: 1.5,
                    }}>
                    About &{' '}
                    <Box component="span" sx={{ color: COMPANY_BRAND_ORANGE }}>
                        Overview
                    </Box>
                </Typography>
                <Typography
                    sx={{
                        color: 'rgba(255,255,255,0.55)',
                        maxWidth: 560,
                        lineHeight: 1.65,
                        fontSize: '0.95rem',
                        fontFamily: FONT_ENUM.MEDIUM_TEXT,
                    }}>
                    Define your company&apos;s cinematic identity. This overview will be the first impression for potential talent and
                    production partners.
                </Typography>
            </Box>

            <Box
                sx={{
                    bgcolor: SURFACE_CARD,
                    borderRadius: 3,
                    p: { xs: 2, sm: 3 },
                    border: '1px solid rgba(255,255,255,0.06)',
                    boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
                }}>
                <Typography
                    sx={{
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: '0.14em',
                        textTransform: 'uppercase',
                        color: LABEL_MUTED,
                        mb: 2,
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                    }}>
                    Company biography
                </Typography>
                <RichTextEditor
                    shellVariant="companyAbout"
                    value={formData.about}
                    onChange={value => {
                        handleFieldChange('about', value);
                    }}
                />
            </Box>

            <Grid container spacing={2}>
                <Grid item xs={12} lg={6}>
                    <Box
                        sx={{
                            bgcolor: SURFACE_CARD,
                            borderRadius: 3,
                            p: { xs: 2, sm: 3 },
                            border: '1px solid rgba(255,255,255,0.06)',
                            boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                        }}>
                        <Box sx={{ mb: 2 }}>
                            <Typography
                                sx={{
                                    fontSize: 11,
                                    fontWeight: 700,
                                    letterSpacing: '0.14em',
                                    textTransform: 'uppercase',
                                    color: LABEL_MUTED,
                                    mb: 0.5,
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                }}>
                                Company perks
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: 11,
                                    color: 'rgba(255,255,255,0.45)',
                                    fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                }}>
                                What makes working here special?
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                            {formData.perks.map(perk => (
                                <Chip
                                    key={perk}
                                    label={perk}
                                    onDelete={isEditing ? () => removePerk(perk) : undefined}
                                    sx={perkChipSx}
                                />
                            ))}
                            {isEditing && (
                                <Box sx={dashedAddRowSx}>
                                    <TextField
                                        placeholder="Add Perk"
                                        variant="standard"
                                        value={newPerk}
                                        onChange={e => setNewPerk(e.target.value)}
                                        onKeyDown={e => e.key === 'Enter' && addPerk()}
                                        fullWidth
                                        InputProps={{
                                            disableUnderline: true,
                                            sx: {
                                                color: '#ffffff',
                                                fontSize: 13,
                                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                '&::placeholder': { color: 'rgba(255,255,255,0.4)', opacity: 1 },
                                            },
                                        }}
                                        sx={{ flex: 1, minWidth: 80 }}
                                    />
                                    <IconButton size="small" onClick={addPerk} aria-label="Submit perk" sx={{ color: '#ffffff' }}>
                                        <Add fontSize="small" />
                                    </IconButton>
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Grid>

                <Grid item xs={12} lg={6}>
                    <Box
                        sx={{
                            bgcolor: SURFACE_CARD,
                            borderRadius: 3,
                            p: { xs: 2, sm: 3 },
                            border: '1px solid rgba(255,255,255,0.06)',
                            boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                        }}>
                        <Box sx={{ mb: 2 }}>
                            <Typography
                                sx={{
                                    fontSize: 11,
                                    fontWeight: 700,
                                    letterSpacing: '0.14em',
                                    textTransform: 'uppercase',
                                    color: LABEL_MUTED,
                                    mb: 0.5,
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                }}>
                                Company values
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: 11,
                                    color: 'rgba(255,255,255,0.45)',
                                    fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                }}>
                                The principles that guide our craft.
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                            {formData.values.map(value => (
                                <Chip
                                    key={value}
                                    label={value}
                                    onDelete={isEditing ? () => removeValue(value) : undefined}
                                    sx={valueChipSx}
                                />
                            ))}
                            {isEditing && (
                                <Box sx={dashedAddRowSx}>
                                    <TextField
                                        placeholder="Add Value"
                                        variant="standard"
                                        value={newValue}
                                        onChange={e => setNewValue(e.target.value)}
                                        onKeyDown={e => e.key === 'Enter' && addValue()}
                                        fullWidth
                                        InputProps={{
                                            disableUnderline: true,
                                            sx: {
                                                color: '#ffffff',
                                                fontSize: 13,
                                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                '&::placeholder': { color: 'rgba(255,255,255,0.4)', opacity: 1 },
                                            },
                                        }}
                                        sx={{ flex: 1, minWidth: 80 }}
                                    />
                                    <IconButton size="small" onClick={addValue} aria-label="Submit value" sx={{ color: '#ffffff' }}>
                                        <Add fontSize="small" />
                                    </IconButton>
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

export { CompanyAboutEdit };
